import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { fromZonedTime } from 'date-fns-tz/fromZonedTime';

/**
 * Convert a naive date + time entered in the Schedule modal into the correct
 * UTC instant, treating the entered wall-clock time as `timeZone`.
 *
 * Why this exists (WRK-5697 follow-up): `new Date('2026-07-17T12:00:00')` parses
 * a string with no offset in the *browser's* local zone, so a scheduler in CET
 * and a colleague in the UK produced different instants for the same "12:00",
 * and the "start is in the past" check landed an hour off during BST. Anchoring
 * to an explicit business timezone (`clientConfig.features.schedulingTimeZone`)
 * makes the instant — and therefore the past check and the value we POST to the
 * backend — identical regardless of the browser's zone.
 *
 * @param dateStr `yyyy-MM-dd` (e.g. the modal's start/end date input)
 * @param timeStr `HH:mm` (e.g. the modal's start/end time input)
 * @param timeZone IANA zone the entered wall time is expressed in
 * @returns the `Date` for that wall-clock moment in `timeZone`
 */
export function zonedWallTimeToInstant(dateStr: string, timeStr: string, timeZone: string): Date {
  return fromZonedTime(`${dateStr}T${timeStr}:00`, timeZone);
}

/** Format an instant as its calendar date (`yyyy-MM-dd`) in `timeZone`. */
export function formatZonedDate(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'yyyy-MM-dd');
}

/** Format an instant as its wall-clock time (`HH:mm`) in `timeZone`. */
export function formatZonedTime(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'HH:mm');
}
